'use client'

import React, { useState, useEffect, useMemo } from 'react';
import { useToaster } from '../context/ToasterContext';
import { Link, useNavigate } from 'react-router-dom';
import Spinner from "../components/Spinner";
import Button from '../components/Button';
import Table from '../components/Table';
import ConfirmationModal from '../components/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates } from '../redux/globalDataSlice';
import api from '../api/axios';
import { strategyOptions } from './templateConstants';

const INPUT_CLS = 'rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';
const EMPTY_FILTERS = { name: '', id: '', routingStrategy: '' };

export default function Templates() {
  const { toaster } = useToaster();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { templates, dataLoading } = useSelector((state) => state.globalData);
  const activeAccountId = useSelector((s) => s.globalData.activeAccountId);

  useEffect(() => { setLoading(dataLoading); }, [dataLoading]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const filteredAndSorted = useMemo(() => {
    let result = [...(templates || [])];

    if (filters.name) {
      result = result.filter(t => t.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.id) {
      result = result.filter(t => t.id?.toLowerCase().includes(filters.id.toLowerCase()));
    }
    if (filters.routingStrategy) {
      result = result.filter(t => t.routingStrategy === filters.routingStrategy);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = String(a[sortConfig.key] ?? '');
        const bVal = String(b[sortConfig.key] ?? '');
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [templates, filters, sortConfig]);

  const initDeleteTemplate = (itemData) => {
    setShowConfirmationModal(true);
    setSelectedItem(itemData);
  };

  const deleteTemplate = async () => {
    try {
      await api.delete(`/mvapi/accounts/${activeAccountId}/templates/${selectedItem.id}`);
      toaster.success('Template deleted successfully!');
      if (activeAccountId) dispatch(fetchTemplates(activeAccountId));
      setShowConfirmationModal(false);
    } catch (error) {
      console.error('Error:', error);
      toaster.error('Something went wrong. Please try again.');
    }
  };

  return (
    <>
      {loading && <Spinner />}

      {!loading && (
        <>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
                  Use Cases
                </h2>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                  Manage all your Use Cases here
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <Button type="button" variant="primary" onClick={() => navigate('/add-template')}>
                  Create new Use Case
                </Button>
              </div>
            </div>

            {/* Filter bar */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Filter by name…"
                value={filters.name}
                onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Filter by ID…"
                value={filters.id}
                onChange={e => setFilters(f => ({ ...f, id: e.target.value }))}
                className={INPUT_CLS}
              />
              <select
                value={filters.routingStrategy}
                onChange={e => setFilters(f => ({ ...f, routingStrategy: e.target.value }))}
                className={INPUT_CLS}
              >
                <option value="">All strategies</option>
                {strategyOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {hasFilters && (
                <button
                  type="button"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="mt-4">
              <Table
                data={filteredAndSorted}
                sortConfig={sortConfig}
                onSort={handleSort}
                onRowClick={(template) => navigate(`/edit-template/${template.id}`)}
                columns={[
                  { key: 'name', title: 'Use Case Name', sortable: true },
                  { key: 'id', title: 'ID', sortable: true },
                  { key: 'route', title: 'Route', condition: true, trueValue: 'Yes', falseValue: 'No' },
                  {
                    key: 'routingStrategy',
                    title: 'Routing Strategy',
                    sortable: true,
                    render: (row) =>
                      strategyOptions.find(o => o.value === row.routingStrategy)?.label ?? row.routingStrategy,
                  },
                  { key: 'notes', title: 'Notes' },
                  {
                    key: 'actions',
                    title: 'Actions',
                    align: 'right',
                    render: (template) => (
                      <div className="space-x-2">
                        <Link
                          to={`/edit-template/${template.id}`}
                          className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-100 mr-5"
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => initDeleteTemplate(template)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    ),
                  },
                ]}
                emptyState={{
                  title: hasFilters ? 'No use cases match your filters' : 'No Use Cases found',
                  description: hasFilters ? 'Try adjusting or clearing the filters.' : 'Get started by creating a new Use Case.',
                }}
              />
            </div>
          </div>
        </>
      )}

      <ConfirmationModal
        open={showConfirmationModal}
        title="Delete template"
        description="Are you sure you want to delete? This action cannot be undone."
        confirmText="Yes"
        cancelText="No"
        onConfirm={deleteTemplate}
        onCancel={() => setShowConfirmationModal(false)}
      />
    </>
  );
}
